/**
 * 设备状态面板组件
 *
 * 显示设备的七大硬件组件健康状态和工作状态
 * 协议版本: V2.0 (v1.0.0_20260105)
 */

import { Row, Col, Typography, Tag, Tooltip } from 'antd'
import {
  CameraOutlined,
  SoundOutlined,
  WifiOutlined,
  ToolOutlined,
  AimOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { DeviceStatus, CameraConfStatus } from '@/store/useDeviceStore'

const { Text } = Typography

interface StatusPanelProps {
  status?: DeviceStatus
  devId: string | null
}

// 工作状态映射
const WORK_STATUS_MAP: Record<number, { label: string; color: string }> = {
  0: { label: '空闲', color: 'default' },
  1: { label: '检测', color: 'processing' },
  2: { label: '跟踪', color: 'success' },
  3: { label: '联合定位', color: 'warning' },
}

// 硬件组件配置
const HARDWARE_ITEMS = [
  { key: 'vis_cam', label: '可见光相机', icon: <CameraOutlined /> },
  { key: 'ir_cam', label: '红外相机', icon: <CameraOutlined /> },
  { key: 'proc_bd', label: '处理板', icon: <ThunderboltOutlined /> },
  { key: 'sound', label: '声传感器', icon: <SoundOutlined /> },
  { key: 'radio', label: '无线电', icon: <WifiOutlined /> },
  { key: 'servo', label: '伺服', icon: <ToolOutlined /> },
  { key: 'laser', label: '测距仪', icon: <AimOutlined /> },
]

function StatusPanel({ status, devId }: StatusPanelProps) {
  if (!devId) {
    return (
      <div style={{ textAlign: 'center', padding: 20 }}>
        <Text type="secondary">请选择设备</Text>
      </div>
    )
  }

  if (!status) {
    return (
      <div style={{ textAlign: 'center', padding: 20 }}>
        <Text type="secondary">暂无状态数据</Text>
      </div>
    )
  }

  // V2.0协议: 根据src_type选择相机配置
  // src_type: 0=可见光, 1=红外
  const currentCameraConf: CameraConfStatus | undefined =
    status.src_type === 0 ? status.vi_conf : status.ir_conf

  // 工作状态从相机配置中获取
  const workSt = currentCameraConf?.work_st ?? 0
  const trackSt = currentCameraConf?.track_st ?? 0
  const workStatus = WORK_STATUS_MAP[workSt] || WORK_STATUS_MAP[0]

  return (
    <div style={{
      fontSize: 13,
      display: 'flex',
      flexDirection: 'column',
      gap: '3px'
    }}>
      {/* 工作状态 */}
      <div style={{
        padding: '5px 8px',
        background: 'linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%)',
        borderRadius: 4,
        border: '1px solid #d9f7be',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        flexShrink: 0
      }}>
        <Text strong style={{ fontSize: 12, color: '#595959' }}>工作状态:</Text>
        <Tag
          color={workStatus.color}
          style={{
            fontSize: 11,
            padding: '2px 8px',
            margin: 0,
            lineHeight: '20px',
            borderRadius: 3
          }}
        >
          {workStatus.label}
        </Tag>
        {workSt === 2 && (
          <Tag
            color="cyan"
            style={{
              fontSize: 11,
              padding: '2px 8px',
              margin: 0,
              lineHeight: '20px',
              borderRadius: 3
            }}
          >
            跟踪: {trackSt === 0 ? '丢失' : trackSt === 1 ? '跟踪中' : '记忆'}
          </Tag>
        )}
      </div>

      {/* 硬件健康状态 */}
      <div style={{
        paddingBottom: 3,
        borderBottom: '1px solid #f0f0f0',
        flexShrink: 0
      }}>
        <Text strong style={{ fontSize: 12, color: '#595959' }}>
          硬件状态
        </Text>
      </div>
      <div className="device-status-panel" style={{ flexShrink: 0 }}>
        {HARDWARE_ITEMS.map((item) => {
          const healthValue = status.health?.[item.key as keyof typeof status.health] ?? 1
          // V2.0协议: 1=正常, 0=损坏（与V1.x相反）
          const isNormal = healthValue === 1

          return (
            <Tooltip key={item.key} title={`${item.label}: ${isNormal ? '正常' : '异常'}`}>
              <div className={`status-card ${isNormal ? '' : 'error'}`}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '3px'
                }}>
                  <span
                    style={{
                      fontSize: 18,
                      color: isNormal ? '#52c41a' : '#ff4d4f',
                      lineHeight: 1,
                    }}
                  >
                    {item.icon}
                  </span>
                  <Text
                    style={{
                      fontSize: 10,
                      lineHeight: 1.2,
                      color: isNormal ? '#666' : '#ff4d4f',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.label}
                  </Text>
                </div>
              </div>
            </Tooltip>
          )
        })}
      </div>

      {/* 吊舱信息 */}
      <div style={{
        paddingTop: 4,
        borderTop: '1px solid #f0f0f0',
        flexShrink: 0
      }}>
        <Text strong style={{ fontSize: 12, color: '#595959' }}>
          吊舱信息
        </Text>
      </div>
      <Row gutter={[6, 6]} style={{ flexShrink: 0 }}>
        {/* 设备位置 (V2.0新增: self_pos) */}
        <Col span={24}>
          <div className="param-card">
            <span className="param-label">设备位置</span>
            <Tooltip title={`纬度: ${(status.self_pos?.[0] || 0).toFixed(6)}, 经度: ${(status.self_pos?.[1] || 0).toFixed(6)}`}>
              <span className="param-value" style={{ fontSize: 11 }}>
                {(status.self_pos?.[0] || 0).toFixed(4)}°, {(status.self_pos?.[1] || 0).toFixed(4)}°
              </span>
            </Tooltip>
          </div>
        </Col>

        {/* 海拔 + 测距 */}
        <Col span={12}>
          <div className="param-card">
            <span className="param-label">海拔</span>
            <span className="param-value">{(status.pot_info?.pot_alti || 0).toFixed(0)}m</span>
          </div>
        </Col>
        <Col span={12}>
          <div className="param-card">
            <span className="param-label">激光测距</span>
            <span className="param-value">{(status.pot_info?.laser_dis || 0).toFixed(0)}m</span>
          </div>
        </Col>

        {/* 伺服角度 */}
        <Col span={6}>
          <div className="param-card">
            <span className="param-label">方位</span>
            <span className="param-value">{(status.pot_info?.servo_az || 0).toFixed(1)}°</span>
          </div>
        </Col>
        <Col span={6}>
          <div className="param-card">
            <span className="param-label">俯仰</span>
            <span className="param-value">{(status.pot_info?.servo_el || 0).toFixed(1)}°</span>
          </div>
        </Col>
        <Col span={6}>
          <div className="param-card">
            <span className="param-label" style={{ color: '#bfbfbf' }}>横滚</span>
            <span className="param-value" style={{ color: '#bfbfbf' }}>/</span>
          </div>
        </Col>
        <Col span={6}>
          <div className="param-card">
            <span className="param-label">温度</span>
            <span className="param-value">
              {typeof status.pot_info?.temperature === 'number'
                ? `${status.pot_info.temperature.toFixed(1)}°C`
                : '--'}
            </span>
          </div>
        </Col>

        {/* 视场角 */}
        <Col span={12}>
          <div className="param-card">
            <span className="param-label">可见光FOV</span>
            <span className="param-value">{(status.pot_info?.vis_fov || 0).toFixed(1)}°</span>
          </div>
        </Col>
        <Col span={12}>
          <div className="param-card">
            <span className="param-label">红外FOV</span>
            <span className="param-value">{(status.pot_info?.ir_fov || 0).toFixed(1)}°</span>
          </div>
        </Col>

        {/* 光学变倍 (V2.0: 从相机配置中获取) */}
        <Col span={12}>
          <div className="param-card">
            <span className="param-label">可见光变倍</span>
            <span className="param-value">{status.vi_conf?.zoom_opt || 0}x</span>
          </div>
        </Col>
        <Col span={12}>
          <div className="param-card">
            <span className="param-label">红外变倍</span>
            <span className="param-value">{status.ir_conf?.zoom_opt || 0}x</span>
          </div>
        </Col>
      </Row>

      {/* 检测/识别状态 - 可见光和红外独立显示 */}
      <div style={{
        paddingTop: 4,
        borderTop: '1px solid #f0f0f0',
        flexShrink: 0
      }}>
        <Text strong style={{ fontSize: 12, color: '#595959' }}>
          检测/识别状态
        </Text>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
        {/* 可见光检测状态 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 8px',
          background: '#fafafa',
          borderRadius: 4,
          fontSize: 12,
        }}>
          <Text style={{ fontSize: 11, color: '#8c8c8c', flexShrink: 0 }}>可见光</Text>
          <Text style={{ fontSize: 11, color: '#595959', flexShrink: 0 }}>检测:</Text>
          <Tag
            color={status.vi_conf?.detect_en ? 'success' : 'default'}
            style={{ fontSize: 10, padding: '1px 6px', lineHeight: '18px', borderRadius: 3, margin: 0 }}
          >
            {status.vi_conf?.detect_en ? '开启' : '关闭'}
          </Tag>
          <Text style={{ fontSize: 11, color: '#595959', flexShrink: 0 }}>AI模型:</Text>
          <Tooltip title={status.vi_conf?.model_name || '未加载'}>
            <Text style={{ fontSize: 10, color: '#1890ff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {status.vi_conf?.model_name || '未加载'}
            </Text>
          </Tooltip>
        </div>
        {/* 红外检测状态 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 8px',
          background: '#fafafa',
          borderRadius: 4,
          fontSize: 12,
        }}>
          <Text style={{ fontSize: 11, color: '#8c8c8c', flexShrink: 0 }}>红外</Text>
          <Text style={{ fontSize: 11, color: '#595959', flexShrink: 0, marginLeft: 10 }}>检测:</Text>
          <Tag
            color={status.ir_conf?.detect_en ? 'success' : 'default'}
            style={{ fontSize: 10, padding: '1px 6px', lineHeight: '18px', borderRadius: 3, margin: 0 }}
          >
            {status.ir_conf?.detect_en ? '开启' : '关闭'}
          </Tag>
          <Text style={{ fontSize: 11, color: '#595959', flexShrink: 0 }}>AI模型:</Text>
          <Tooltip title={status.ir_conf?.model_name || '未加载'}>
            <Text style={{ fontSize: 10, color: '#1890ff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {status.ir_conf?.model_name || '未加载'}
            </Text>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

export default StatusPanel
